import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiCalendar,
    FiClock,
    FiUsers,
    FiMapPin,
    FiStar,
    FiCheckCircle,
    FiAward,
    FiAlertCircle,
    FiChevronRight,
} from 'react-icons/fi';
import BookingSection from './BookingSection';
import './PackageDetailsPage.css';
import Navbar from '../../../Components/Navber';

// --- Breadcrumb Component ---
const Breadcrumb = ({ items }) => (
    <nav className="pkgd-breadcrumb">
        <div className="pkgd-breadcrumb-inner">
            {items.map((item, index) => (
                <span key={index} className="pkgd-breadcrumb-item">
                    {index > 0 && <FiChevronRight className="pkgd-breadcrumb-sep" />}
                    {item.href ? (
                        <Link to={item.href}>{item.label}</Link>
                    ) : (
                        <span className="pkgd-breadcrumb-current">{item.label}</span>
                    )}
                </span>
            ))}
        </div>
    </nav>
);

// --- Meal Plan Card Component ---
const MealPlanCard = ({ mealPlan }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const groupByCategory = (items) => {
        const groups = {};
        items.forEach(item => {
            const cat = item.category_label || item.category || 'Other';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(item);
        });
        return groups;
    };

    const vegGroups = mealPlan.veg_menu?.length ? groupByCategory(mealPlan.veg_menu) : {};
    const nonVegGroups = mealPlan.nonveg_menu?.length ? groupByCategory(mealPlan.nonveg_menu) : {};

    return (
        <div
            className={`pkgd-meal-card ${isExpanded ? 'pkgd-expanded' : ''}`}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
        >
            <div className="pkgd-meal-card-header">
                <div className="pkgd-meal-type-badge">
                    {mealPlan.plan_type.charAt(0).toUpperCase() + mealPlan.plan_type.slice(1)}
                </div>
                <h4 className="pkgd-meal-name">{mealPlan.plan_name}</h4>
            </div>

            <p className="pkgd-meal-description">{mealPlan.description}</p>

            <div className="pkgd-meal-meta">
                <div className="pkgd-meal-meta-item">
                    <FiUsers />
                    <span>{mealPlan.min_persons} - {mealPlan.max_persons} guests</span>
                </div>
                <div className="pkgd-meal-meta-item">
                    <FiStar />
                    <span>₹{mealPlan.price_per_person} / person</span>
                </div>
            </div>

            <div className="pkgd-meal-tags">
                {mealPlan.has_veg_menu && (
                    <span className="pkgd-tag pkgd-tag-veg">Veg</span>
                )}
                {mealPlan.has_nonveg_menu && (
                    <span className="pkgd-tag pkgd-tag-nonveg">Non-Veg</span>
                )}
                {mealPlan.max_veg_items > 0 && (
                    <span className="pkgd-tag">{mealPlan.max_veg_items} Veg Items</span>
                )}
                {mealPlan.max_nonveg_items > 0 && (
                    <span className="pkgd-tag">{mealPlan.max_nonveg_items} Non-Veg Items</span>
                )}
            </div>

            {isExpanded && (
                <div className="pkgd-meal-expanded">
                    <div className="pkgd-meal-expanded-content">
                        <h5>Menu Items</h5>
                        {Object.keys(vegGroups).length === 0 && Object.keys(nonVegGroups).length === 0 ? (
                            <p>No menu items defined for this plan.</p>
                        ) : (
                            <>
                                {Object.keys(vegGroups).length > 0 && (
                                    <div className="pkgd-menu-group">
                                        <h6>Veg</h6>
                                        {Object.entries(vegGroups).map(([category, items]) => (
                                            <div key={category}>
                                                <strong>{category}</strong>
                                                <ul>
                                                    {items.map(item => (
                                                        <li key={item.id}>{item.item_name}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {Object.keys(nonVegGroups).length > 0 && (
                                    <div className="pkgd-menu-group">
                                        <h6>Non‑Veg</h6>
                                        {Object.entries(nonVegGroups).map(([category, items]) => (
                                            <div key={category}>
                                                <strong>{category}</strong>
                                                <ul>
                                                    {items.map(item => (
                                                        <li key={item.id}>{item.item_name}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Special Request Card ---
const SpecialRequestCard = ({ request }) => (
    <div className="pkgd-special-card">
        <div className="pkgd-special-icon">
            <FiStar />
        </div>
        <div className="pkgd-special-content">
            <h4>{request.title || 'Custom Request'}</h4>
            <p>{request.description || 'Special arrangements can be made upon request.'}</p>
        </div>
    </div>
);

// --- Main Component ---
export default function PackageDetailsPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const bookingRef = useRef(null);

    const [packageData, setPackageData] = useState(null);
    const [mealPlans, setMealPlans] = useState([]);
    const [specialRequests, setSpecialRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [hideMobileBar, setHideMobileBar] = useState(false);

    useEffect(() => {
        const fetchPackageDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const pkgResponse = await fetch(
                    `https://admin.jayrajgroup.com/api/wedding/packages/${slug}`
                );
                if (!pkgResponse.ok) throw new Error(`HTTP error! status: ${pkgResponse.status}`);
                const pkgResult = await pkgResponse.json();

                if (!pkgResult.status || !pkgResult.data) {
                    throw new Error(pkgResult.message || 'Failed to fetch package data');
                }

                const pkg = pkgResult.data;
                setPackageData(pkg);

                const mealResponse = await fetch(
                    `https://admin.jayrajgroup.com/api/wedding/packages/${pkg.id}/meal-plans`
                );
                if (mealResponse.ok) {
                    const mealResult = await mealResponse.json();
                    if (mealResult.status && mealResult.data) {
                        setMealPlans(mealResult.data);
                    }
                }

                const specialResponse = await fetch(
                    `https://admin.jayrajgroup.com/api/wedding/packages/${pkg.id}/special-requests`
                );
                if (specialResponse.ok) {
                    const specialResult = await specialResponse.json();
                    if (specialResult.status && specialResult.data) {
                        setSpecialRequests(specialResult.data);
                    }
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (slug) fetchPackageDetails();
    }, [slug]);

    // --- Hide mobile bottom bar when booking section enters view ---
    useEffect(() => {
        if (!bookingRef.current) return undefined;
        if (typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setHideMobileBar(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '0px 0px -15% 0px',
                threshold: 0,
            }
        );

        observer.observe(bookingRef.current);
        return () => observer.disconnect();
    }, [packageData, loading]);

    const handleGoBack = () => navigate(-1);

    const destinationName = packageData?.destination
        ? `${packageData.destination.city?.name || ''}, ${packageData.destination.state?.name || ''}`
        : '';

    // --- Loading ---
    if (loading) {
        return (
            <div className="pkgd-page-wrapper">
                <Navbar />
                <div className="pkgd-container">
                    <div className="pkgd-loading-state">
                        <div className="pkgd-skeleton-hero"></div>
                        <div className="pkgd-skeleton-content">
                            <div className="pkgd-skeleton-title"></div>
                            <div className="pkgd-skeleton-text"></div>
                            <div className="pkgd-skeleton-grid">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="pkgd-skeleton-card"></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !packageData) {
        return (
            <div className="pkgd-page-wrapper">
                <Navbar />
                <div className="pkgd-container">
                    <div className="pkgd-error-state">
                        <FiAlertCircle className="pkgd-error-icon" />
                        <h3>Package Not Found</h3>
                        <p>{error || 'The package you are looking for does not exist.'}</p>
                        <button onClick={handleGoBack} className="pkgd-btn-primary">
                            <FiArrowLeft /> Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const hasMealPlans = mealPlans.length > 0;
    const hasSpecialRequests = specialRequests.length > 0;

    return (
        <div className="pkgd-page-wrapper">
            <Navbar />

            <div className="pkgd-container">
                <Breadcrumb
                    items={[
                        { label: 'Home', href: '/destination-wedding' },
                        { label: 'Destinations', href: '/wedding-destinations' },
                        {
                            label: packageData.destination?.name || 'Packages',
                            href: `/destination-wedding-packages/${packageData.destination?.id}`,
                        },
                        { label: packageData.name },
                    ]}
                />

                {/* Hero */}
                <section className="pkgd-hero">
                    <div
                        className="pkgd-hero-bg"
                        style={{
                            backgroundImage: `url(${packageData.banner_url || packageData.featured_photo_url})`,
                        }}
                    >
                        <div className="pkgd-hero-overlay"></div>
                    </div>
                    <div className="pkgd-hero-content">
                        <button className="pkgd-btn-back" onClick={handleGoBack}>
                            <FiArrowLeft /> Back
                        </button>
                        <div className="pkgd-hero-text">
                            <h1 className="pkgd-hero-title">{packageData.name}</h1>
                            <p className="pkgd-hero-description">
                                {packageData.short_description || 'Experience the perfect wedding celebration'}
                            </p>
                            <div className="pkgd-hero-stats">
                                <div className="pkgd-stat-item">
                                    <FiCalendar /> <span>{packageData.duration_days} Days</span>
                                </div>
                                <div className="pkgd-stat-item">
                                    <FiClock /> <span>{packageData.duration_nights} Nights</span>
                                </div>
                                <div className="pkgd-stat-item">
                                    <FiMapPin /> <span>{destinationName || 'Various Locations'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main Grid */}
                <div className="pkgd-main-grid">
                    <div className="pkgd-details-column">
                        {/* Tabs */}
                        <div className="pkgd-tabs">
                            <button
                                className={`pkgd-tab-btn ${activeTab === 'overview' ? 'pkgd-active' : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                Overview
                            </button>
                            <button
                                className={`pkgd-tab-btn ${activeTab === 'meal-plans' ? 'pkgd-active' : ''}`}
                                onClick={() => setActiveTab('meal-plans')}
                            >
                                Meal Plans
                                {hasMealPlans && <span className="pkgd-tab-count">{mealPlans.length}</span>}
                            </button>
                            <button
                                className={`pkgd-tab-btn ${activeTab === 'special-requests' ? 'pkgd-active' : ''}`}
                                onClick={() => setActiveTab('special-requests')}
                            >
                                Special Requests
                                {hasSpecialRequests && <span className="pkgd-tab-count">{specialRequests.length}</span>}
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="pkgd-tab-content">
                            {activeTab === 'overview' && (
                                <div className="pkgd-overview">
                                    <div
                                        className="pkgd-description"
                                        dangerouslySetInnerHTML={{
                                            __html: packageData.description || '<p>No description available.</p>',
                                        }}
                                    />

                                    {packageData.inclusions_note && (
                                        <div className="pkgd-inclusions">
                                            <h3>What's Included</h3>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: packageData.inclusions_note,
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className="pkgd-highlights">
                                        <h3>Package Highlights</h3>
                                        <div className="pkgd-highlights-grid">
                                            <div className="pkgd-highlight-item">
                                                <FiAward /> <span>Premium Venue</span>
                                            </div>
                                            <div className="pkgd-highlight-item">
                                                <FiUsers /> <span>Expert Planners</span>
                                            </div>
                                            <div className="pkgd-highlight-item">
                                                <FiCalendar /> <span>Flexible Dates</span>
                                            </div>
                                            <div className="pkgd-highlight-item">
                                                <FiStar /> <span>Luxury Services</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'meal-plans' && (
                                <div className="pkgd-meal-plans">
                                    {hasMealPlans ? (
                                        <div className="pkgd-meal-grid">
                                            {mealPlans.map((plan) => (
                                                <MealPlanCard key={plan.id} mealPlan={plan} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="pkgd-empty-state">
                                            <FiAlertCircle />
                                            <p>No meal plans available for this package.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'special-requests' && (
                                <div className="pkgd-special-requests">
                                    {hasSpecialRequests ? (
                                        <div className="pkgd-special-grid">
                                            {specialRequests.map((request, index) => (
                                                <SpecialRequestCard key={index} request={request} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="pkgd-empty-state">
                                            <FiAlertCircle />
                                            <p>No special requests available.</p>
                                            <p className="pkgd-empty-sub">You can make custom requests during booking.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Booking Section wrapped with ref for scroll + observer */}
                    <div ref={bookingRef} id="pkgd-booking-anchor">
                        <BookingSection
                            packageData={packageData}
                            destinationName={destinationName}
                            mealPlans={mealPlans}
                            specialRequests={specialRequests}
                        />
                    </div>
                </div>
            </div>

            {/* Mobile sticky bottom bar — duration left, Book Now right */}
            <div
                className={`pkgd-mobile-bar ${hideMobileBar ? 'pkgd-mobile-bar-hidden' : ''}`}
                role="region"
                aria-label="Quick actions"
                aria-hidden={hideMobileBar}
            >
                <div className="pkgd-mobile-bar-duration">
                    <FiCalendar aria-hidden="true" />
                    <span>
                        {packageData.duration_days}D
                        <span className="pkgd-mobile-bar-sep">/</span>
                        {packageData.duration_nights}N
                    </span>
                </div>

                <a
                    href="#pkgd-booking-anchor"
                    className="pkgd-mobile-bar-cta"
                    onClick={(e) => {
                        e.preventDefault();
                        bookingRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }}
                >
                    Book Now
                    <FiChevronRight aria-hidden="true" />
                </a>
            </div>
        </div>
    );
}